import { useState } from 'react'
import dayjs from 'dayjs'
import {
  AgxBadge,
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import {
  Deposit,
  FormCondition,
  FormConditionCondition,
} from '@urbanx/agx.campaigns.client'
import ConditionDate from '../components/ConditionDate'
import { EditConditionModal } from '../modals/EditConditionModal'
import { useFormConditionDueDate } from '../components/useFormConditionDueDate'
import slugify from 'slugify'

interface ConditionPanelProps {
  id: string
  panelTitle: string
  contractDate?: Date
  condition?: FormCondition
  onChange: (condition: FormCondition | Deposit | undefined) => void
  onDelete?: () => void
  hideEditButton?: boolean
  hideConditionInfo?: boolean
  hideDeleteButton?: boolean
  onDateConditions?: FormConditionCondition[]
  fromDateConditions?: FormConditionCondition[]
  specialConditionIdToClauseNumberMap: {
    [key: string]: string
  }
}

export function ConditionPanel({
  id,
  panelTitle,
  contractDate,
  condition,
  onChange,
  onDelete,
  hideEditButton,
  hideConditionInfo,
  hideDeleteButton,
  onDateConditions,
  fromDateConditions,
  specialConditionIdToClauseNumberMap,
}: ConditionPanelProps) {
  const [showEditDateModal, setShowEditDateModal] = useState(false)
  const dueDate = useFormConditionDueDate(condition, contractDate)

  if (
    !condition?.condition ||
    [
      FormConditionCondition.NotSubjectToFinance,
      FormConditionCondition.NotApplicable,
    ].includes(condition.condition)
  )
    return

  const dueDateValue = dueDate?.dueDate
    ? dayjs(dueDate.dueDate).format('YYYY-MM-DD')
    : ''

  return (
    <AgxCollapsible
      key={`deposit-panel-${id}`}
      variant={'withDivider'}
      enableToggle
      open
      customTitle={
        <AgxSpacer
          fill
          direction="horizontal"
          gap="xs"
          contentAlignment="center"
        >
          <AgxTypography
            id={`condition-${slugify(panelTitle)}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {panelTitle}
          </AgxTypography>
          {dueDateValue && (
            <AgxBadge
              type="colourfill"
              colour="yellow"
              size="md"
              text={dayjs(dueDateValue).format('Do MMM')}
            />
          )}
        </AgxSpacer>
      }
      content={
        <>
          {!hideConditionInfo && condition && (
            <>
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                fill
                padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
              >
                <ConditionDate
                  id={id}
                  condition={condition}
                  specialConditionIdToClauseNumberMap={
                    specialConditionIdToClauseNumberMap ?? {}
                  }
                />
              </AgxSpacer>
              <AgxDivider variant="horizontal" />
            </>
          )}
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            fill
            padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
            contentAlignment="end"
          >
            <AgxDatePicker
              id={`${id}-due-date`}
              stretch
              disabled={true}
              optionalText=""
              label="Due Date"
              key={dueDateValue}
              value={dueDateValue}
            />
            {!hideEditButton && (
              <AgxButton
                id={`${id}-edit-due-date`}
                variant="hollow"
                text="Edit"
                onClick={() => {
                  setShowEditDateModal(true)
                }}
              />
            )}
          </AgxSpacer>
          {(!hideConditionInfo || !hideDeleteButton) && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-md"
              fill
              padding={{ top: 'spacing-md', bottom: 'spacing-md' }}
              contentAlignment="end"
              contentJustification="end"
            >
              {!hideDeleteButton && (
                <AgxButton
                  id={`${id}-delete`}
                  variant="link"
                  danger
                  text="Delete"
                  onClick={onDelete}
                />
              )}
            </AgxSpacer>
          )}
          {showEditDateModal && condition && (
            <EditConditionModal
              id={id}
              modalTitle="Edit Condition"
              formCondition={condition}
              onDateConditions={
                onDateConditions ?? [
                  FormConditionCondition.OnSpecificDate,
                  FormConditionCondition.UponSatisfactionOfSpecialCondition,
                ]
              }
              fromDateConditions={
                fromDateConditions ?? [
                  FormConditionCondition.NdaysFromDate,
                  FormConditionCondition.UponSatisfactionOfSpecialCondition,
                ]
              }
              openModal={setShowEditDateModal}
              onChange={onChange}
            />
          )}
        </>
      }
    ></AgxCollapsible>
  )
}
